import { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import VehicleCard from "./vehicleCard";
import { AccountsContext } from "context/AccountsContext";
import axios from "axios";
import { cachementreportendpoint } from "config";
import Button from "@mui/material/Button";

function VehicleList({ onJourneysChange }) {
  const { fetchThings, things } = useContext(AccountsContext);
  const [selectedVehicles, setSelectedVehicles] = useState([]); // Array to track vehicles with their journeys
  const [selectedJourneys, setSelectedJourneys] = useState({}); // Combined selections
  const [showAddVehicle, setShowAddVehicle] = useState(true);

  useEffect(() => {
    fetchThings();
  }, []);

  // Fetch report API when a new vehicle is selected
  const handleVehicleSelect = async (newValue) => {
    if (!newValue) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${cachementreportendpoint}/${newValue.thingId}?thingId=${newValue.thingId}`,
        {
          headers: {
            "x-dhundhoo-session": token,
          },
        }
      );
      console.log("API Response:", response.data);

      if (response.data?.allWayPoints) {
        const newJourneys = response.data.allWayPoints;

        // FIX: Naye vehicle ko array mein sabse pehle (index 0 par) add kiya hai
        const updatedVehicles = [
          { ...newValue, journeys: newJourneys }, // New vehicle on top
          ...selectedVehicles,
        ];
        setSelectedVehicles(updatedVehicles);

        // Update selectedJourneys with new journey IDs
        const allSelectedJourneys = { ...selectedJourneys };
        newJourneys.forEach((journey) => {
          if (!allSelectedJourneys[journey.journeyId]) {
            allSelectedJourneys[journey.journeyId] = false; // Initialize as unselected
          }
        });

        setSelectedJourneys(allSelectedJourneys);
        onJourneysChange(
          updatedVehicles.flatMap((v) => v.journeys), // Pass combined journeys
          allSelectedJourneys
        );

        setShowAddVehicle(false); // Input hide karke button wapas dikhao
      }
    } catch (error) {
      console.error("Error fetching vehicle report:", error);
    }
  };

  // Toggle checkbox selection
  const handleCheckboxChange = (journeyId) => {
    setSelectedJourneys((prev) => {
      const newSelectedJourneys = {
        ...prev,
        [journeyId]: !prev[journeyId],
      };
      onJourneysChange(
        selectedVehicles.flatMap((v) => v.journeys),
        newSelectedJourneys
      );
      return newSelectedJourneys;
    });
  };

  // Remove a vehicle and its associated journeys
  const handleRemoveVehicle = (vehicleToRemove) => {
    const updatedVehicles = selectedVehicles.filter(
      (vehicle) => vehicle.thingId !== vehicleToRemove.thingId
    );
    const removedJourneyIds = vehicleToRemove.journeys.map((j) => j.journeyId);
    const updatedSelectedJourneys = { ...selectedJourneys };
    removedJourneyIds.forEach((id) => delete updatedSelectedJourneys[id]);

    setSelectedVehicles(updatedVehicles);
    setSelectedJourneys(updatedSelectedJourneys);
    onJourneysChange(
      updatedVehicles.flatMap((v) => v.journeys),
      updatedSelectedJourneys
    );

    if (updatedVehicles.length === 0) {
      setShowAddVehicle(true);
    }
  };

  return (
    <MDBox display="flex" flexDirection="column" height="100%">

      {/* Fixed Top Search Layout */}
      <MDBox mb={2} sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 2 }}>
        {selectedVehicles.length > 0 && !showAddVehicle ? (
          <Button
            variant="contained"
            fullWidth
            onClick={() => setShowAddVehicle(true)}
            sx={{ mb: 1, color: "#fff" }}
          >
            Add Another Vehicle
          </Button>
        ) : (
          <Autocomplete
            disablePortal
            options={things.filter(
              (thing) =>
                !selectedVehicles.some((v) => v.thingId === thing.thingId)
            )}
            getOptionLabel={(option) => option.nickName || option.thingId}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase().trim();

              return options.filter((option) => {
                const name = (option.name || "").toLowerCase();
                const nickName = (option.nickName || "").toLowerCase();
                const thingId = (option.thingId || "").toLowerCase();

                return (
                  name.includes(searchValue) ||
                  nickName.includes(searchValue) ||
                  thingId.includes(searchValue)
                );
              });
            }}
            value={null}
            onChange={(event, newValue) => handleVehicleSelect(newValue)}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <span>{option.nickName || option.name || "-"}</span>
                <span style={{ color: "#666" }}>{option.thingId}</span>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedVehicles.length > 0
                    ? "Search or select another vehicle"
                    : "Search or select vehicle"
                }
                fullWidth
                size="small"
              />
            )}
          />
        )}
      </MDBox>

      {/* Scrollable Vehicles List Area */}
      <MDBox
        sx={{
          maxHeight: { xs: "400px", md: "650px" },
          overflowY: "auto",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: "4px",
          },
        }}
      >
        {selectedVehicles.map((vehicle) => (
          <Grid container spacing={2} key={vehicle.thingId} mb={2}>
            <Grid item xs={12}>
              <VehicleCard
                title={vehicle.nickName || vehicle.thingId}
                journeys={vehicle.journeys}
                selectedJourneys={selectedJourneys}
                onToggleJourney={handleCheckboxChange}
                onDelete={() => handleRemoveVehicle(vehicle)}
              />
            </Grid>
          </Grid>
        ))}
      </MDBox>

    </MDBox>
  );
}

VehicleList.propTypes = {
  onJourneysChange: PropTypes.func.isRequired,
};

export default VehicleList;